const ALLOWED_TAGS = new Set([
  'BR',
  'STRONG',
  'EM',
  'B',
  'I',
  'U',
  'P',
  'DIV',
  'SPAN',
  'UL',
  'OL',
  'LI',
  'CODE',
  'PRE',
  'TABLE',
  'THEAD',
  'TBODY',
  'TR',
  'TH',
  'TD',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
]);

const ALLOWED_ATTRS = new Set(['class', 'colspan', 'rowspan']);

export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function unwrapNode(node) {
  const parent = node.parentNode;
  if (!parent) return;

  while (node.firstChild) {
    parent.insertBefore(node.firstChild, node);
  }
  parent.removeChild(node);
}

function sanitizeAttributes(element) {
  for (const attr of Array.from(element.attributes)) {
    const name = attr.name.toLowerCase();
    const value = attr.value;

    if (!ALLOWED_ATTRS.has(name)) {
      element.removeAttribute(attr.name);
      continue;
    }

    if (name === 'class') {
      const safeClasses = value
        .split(/\s+/)
        .filter(Boolean)
        .filter((token) => /^[a-zA-Z0-9_-]+$/.test(token))
        .join(' ');

      if (safeClasses) {
        element.setAttribute('class', safeClasses);
      } else {
        element.removeAttribute('class');
      }
      continue;
    }

    if ((name === 'colspan' || name === 'rowspan') && !/^\d+$/.test(value)) {
      element.removeAttribute(attr.name);
    }
  }
}

function sanitizeNode(node) {
  for (const child of Array.from(node.childNodes)) {
    sanitizeNode(child);
  }

  if (node.nodeType === Node.COMMENT_NODE) {
    node.remove();
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return;
  }

  const element = /** @type {HTMLElement} */ (node);
  if (!ALLOWED_TAGS.has(element.tagName)) {
    unwrapNode(element);
    return;
  }

  sanitizeAttributes(element);
}

export function sanitizeRichHtml(html) {
  const input = String(html ?? '');
  if (!input) return '';

  if (
    typeof window === 'undefined' ||
    typeof document === 'undefined' ||
    typeof DOMParser === 'undefined' ||
    typeof Node === 'undefined'
  ) {
    return escapeHtml(input);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<body>${input}</body>`, 'text/html');
  const body = doc.body;
  if (!body) {
    return escapeHtml(input);
  }

  for (const child of Array.from(body.childNodes)) {
    sanitizeNode(child);
  }

  return body.innerHTML;
}
